import { useOnline } from "../../hooks/useOnline";
import OfflineNotice from "../OfflineNotice/OfflineNotice";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock } from "@fortawesome/free-solid-svg-icons";
import { useAuth } from "../../context/AuthContext";
import { sendAssistantMessage, AssistantMessage, AssistantQuotaError } from "../../utils/assistantChat";
import TurtleIcon from "./TurtleIcon";
import { useLocalizedPath } from "../../i18n/localizedPath";
import { isDesktopLocalProfile } from "../../utils/desktopProfile";
import DesktopUnavailable from "../DesktopUnavailable";
import styles from "./assistantWidget.module.scss";

const STUDY_SESSION_SECONDS = 5 * 60;

const SUGGESTIONS: { labelKey: string; path: string }[] = [
    { labelKey: "assistant.suggestions.grammar", path: "/grammar" },
    { labelKey: "assistant.suggestions.kana", path: "/kana-writing-practice" },
    { labelKey: "assistant.suggestions.kanji", path: "/kanji-memory-quiz" },
    { labelKey: "assistant.suggestions.translate", path: "/translate" },
];

function formatSeconds(totalSeconds: number) {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

// Offline desktop build only (see utils/desktopProfile.ts): the turtle stays, but opening it says the
// AI assistant needs the internet instead of offering a chat that could never answer.
function DesktopAssistantUnavailable() {
    const { t } = useTranslation("common");
    const [isOpen, setIsOpen] = useState(false);
    return (
        <div className={styles.widget}>
            {isOpen && (
                <div className={styles.panel} role="dialog" aria-label={t("assistant.dialogLabel")}>
                    <div className={styles.header}>
                        <span className={styles.title}><TurtleIcon size={22} />かめ</span>
                        <button type="button" className={styles.closeButton} onClick={() => setIsOpen(false)} aria-label={t("assistant.close")}>×</button>
                    </div>
                    <DesktopUnavailable feature="assistant" inline />
                </div>
            )}
            <button type="button" className={styles.toggleButton} onClick={() => setIsOpen((open) => !open)} aria-label={isOpen ? t("assistant.close") : t("assistant.open")}>
                <TurtleIcon size={68} />
            </button>
        </div>
    );
}

// App-wide floating assistant, gated to signed-in users only (no redirect -
// it simply doesn't render for guests/loading, since it's a global overlay
// rather than a routed page). See src/utils/assistantChat.ts for the
// Cognito-gated, rate-limited backend it talks to.
function AssistantWidget() {
    const online = useOnline();
    const { t } = useTranslation("common");
    const lp = useLocalizedPath();
    const { status, getIdToken } = useAuth();
    const navigate = useNavigate();
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState<AssistantMessage[]>([]);
    const [input, setInput] = useState("");
    const [isSending, setIsSending] = useState(false);
    const [studySecondsLeft, setStudySecondsLeft] = useState<number | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView?.({ block: "end" });
    }, [messages, isSending, isOpen, studySecondsLeft]);

    // Ticks down independently of whether the panel is open, so reopening
    // it mid-session shows the correct time remaining instead of resetting.
    useEffect(() => {
        if (studySecondsLeft === null) return undefined;
        if (studySecondsLeft <= 0) {
            setStudySecondsLeft(null);
            setMessages((current) => [...current, { role: "assistant", content: t("assistant.studyCongrats") }]);
            return undefined;
        }
        const timeoutId = window.setTimeout(() => setStudySecondsLeft((seconds) => (seconds ?? 0) - 1), 1000);
        return () => window.clearTimeout(timeoutId);
    }, [studySecondsLeft, t]);

    if (status !== "authenticated") return null;
    if (isDesktopLocalProfile) return <DesktopAssistantUnavailable />;

    async function handleSend(rawText: string) {
        const text = rawText.trim();
        if (!online || navigator.onLine === false || !text || isSending) return;

        const nextMessages: AssistantMessage[] = [...messages, { role: "user", content: text }];
        setMessages(nextMessages);
        setInput("");
        setIsSending(true);

        try {
            const idToken = await getIdToken();
            const reply = await sendAssistantMessage(nextMessages, idToken);
            setMessages((current) => [...current, { role: "assistant", content: reply }]);
        } catch (err) {
            const content = err instanceof AssistantQuotaError ? t("assistant.quota") : t("assistant.error");
            setMessages((current) => [...current, { role: "assistant", content }]);
        } finally {
            setIsSending(false);
        }
    }

    function handleSuggestion(path: string) {
        setIsOpen(false);
        navigate(lp(path));
    }

    function handleStartStudySession() {
        setMessages((current) => [...current, { role: "assistant", content: t("assistant.studyIntro") }]);
        setStudySecondsLeft(STUDY_SESSION_SECONDS);
    }

    function handleStopStudySession() {
        setStudySecondsLeft(null);
    }

    function handleBackToMenu() {
        setMessages([]);
        setStudySecondsLeft(null);
    }

    return (
        <div className={styles.widget}>
            {isOpen && (
                <div className={styles.panel} role="dialog" aria-label={t("assistant.dialogLabel")}>
                    <div className={styles.header}>
                        <span className={styles.title}>
                            <TurtleIcon size={22} />
                            かめ
                            <span className={styles.aiBadge}>{t("assistant.aiBadge")}</span>
                        </span>
                        <button
                            type="button"
                            className={styles.closeButton}
                            onClick={() => setIsOpen(false)}
                            aria-label={t("assistant.close")}
                        >
                            ×
                        </button>
                    </div>

                    <div className={styles.messages}>
                        <div className={`${styles.bubble} ${styles.assistantBubble}`}>{t("assistant.greeting")}</div>
                        {messages.map((message, index) => (
                            <div
                                key={index}
                                className={`${styles.bubble} ${
                                    message.role === "user" ? styles.userBubble : styles.assistantBubble
                                }`}
                            >
                                {message.content}
                            </div>
                        ))}
                        {isSending && (
                            <div className={`${styles.bubble} ${styles.assistantBubble}`} aria-label={t("assistant.typing")}>
                                …
                            </div>
                        )}
                        {studySecondsLeft !== null && (
                            <div className={styles.studyTimer} role="timer" aria-live="polite">
                                <FontAwesomeIcon icon={faClock} className={styles.studyTimerIcon} />
                                <span className={styles.studyTimerValue}>{formatSeconds(studySecondsLeft)}</span>
                                <button type="button" className={styles.studyTimerStop} onClick={handleStopStudySession}>
                                    {t("assistant.stop")}
                                </button>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>

                    {messages.length === 0 && (
                        <div className={styles.suggestions}>
                            <button
                                type="button"
                                className={styles.suggestionChip}
                                onClick={handleStartStudySession}
                            >
                                {t("assistant.studyIntro")}
                            </button>
                            {SUGGESTIONS.map((suggestion) => (
                                <button
                                    key={suggestion.path}
                                    type="button"
                                    className={styles.suggestionChip}
                                    onClick={() => handleSuggestion(suggestion.path)}
                                >
                                    {t(suggestion.labelKey)}
                                </button>
                            ))}
                        </div>
                    )}

                    {messages.length > 0 && (
                        <div className={styles.suggestions}>
                            <button
                                type="button"
                                className={styles.suggestionChip}
                                onClick={handleBackToMenu}
                                disabled={isSending}
                            >
                                <span aria-hidden="true">←</span> {t("assistant.backToMenu")}
                            </button>
                        </div>
                    )}

                    {/* Always visible (not only in the first message): AI answers can be wrong. */}
                    <p className={styles.aiNotice}>{t("assistant.aiNotice")}</p>
                    <OfflineNotice />
                    <form
                        className={styles.inputRow}
                        onSubmit={(event) => {
                            event.preventDefault();
                            handleSend(input);
                        }}
                    >
                        <input
                            type="text"
                            value={input}
                            onChange={(event) => setInput(event.target.value)}
                            placeholder={t("assistant.placeholder")}
                            aria-label={t("assistant.messageLabel")}
                            disabled={isSending}
                        />
                        <button type="submit" disabled={!online || isSending || !input.trim()}>
                            {t("assistant.send")}
                        </button>
                    </form>
                </div>
            )}

            <button
                type="button"
                className={styles.toggleButton}
                onClick={() => setIsOpen((open) => !open)}
                aria-label={isOpen ? t("assistant.close") : t("assistant.open")}
            >
                <TurtleIcon size={68} />
            </button>
        </div>
    );
}

export default AssistantWidget;
